import { Autocomplete, TextField, CircularProgress, SxProps, Theme } from '@mui/material';
import { useController } from 'react-hook-form';
import { HTMLUListElementAttributes } from 'shared/types';

interface SelectFieldProps<T> {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  label: string;
  rules?: Object;
  error?: boolean;
  helperText?: string;
  readOnly?: boolean;
  options: T[];
  getOptionLabel(option: T): string;
  isOptionEqualToValue(option: T, value: T): boolean;
  loading?: boolean;
  defaultValue?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  multiple?: boolean;
  disableCloseOnSelect?: boolean;
  onSelectChange?: (value: T | T[] | null) => void;
}

export function SelectField<T>({
  name,
  label,
  rules,
  error,
  helperText,
  readOnly,
  options,
  getOptionLabel,
  isOptionEqualToValue,
  loading,
  fullWidth,
  defaultValue = '',
  disabled,
  sx,
  multiple,
  disableCloseOnSelect,
  onSelectChange,
}: SelectFieldProps<T>) {
  const {
    field: { ref, value, onChange },
  } = useController({ name, defaultValue, rules });

  return (
    <Autocomplete
      value={value}
      id={name}
      onChange={(_, value) => {
        if (onSelectChange) {
          onSelectChange(value);
        }
        onChange(value);
      }}
      disabled={disabled}
      options={options}
      fullWidth={fullWidth}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      data-testid={`${name}-combobox`}
      sx={sx}
      multiple={multiple}
      disableCloseOnSelect={disableCloseOnSelect}
      ListboxProps={{ 'data-testid': `${name}-listbox` } as HTMLUListElementAttributes}
      renderInput={params => (
        <TextField
          variant='outlined'
          {...params}
          label={label}
          error={error}
          helperText={helperText}
          inputRef={ref}
          InputProps={{
            ...params.InputProps,
            endAdornment: loading ? <CircularProgress color='inherit' size={20} /> : params.InputProps.endAdornment,
            readOnly,
          }}
        />
      )}
    />
  );
}
